import React, { Fragment, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Row, Col, Card, Modal, Form, Button } from 'react-bootstrap';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useSignalR } from '../../services/signalR/SignalRProvider';
import { useLanguage } from '../../services/language/LanguageContext';
import { Link } from 'react-router-dom';
import { DateTimeFormat } from '../../helper/Formatters';
import ImageUpload from '../../components/image-upload';
import ImageViewer from '../../components/image-viewer/ImageViewer';
import Pagination from '../../components/pagination';

const StatusPill = ({ color, label }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px 3px 7px',
        background: color ? `${color}18` : '#f3f4f6',
        border: `1px solid ${color ? `${color}40` : '#e5e7eb'}`,
        borderRadius: 20, fontSize: 12, fontWeight: 600,
        color: color || '#6b7280', whiteSpace: 'nowrap',
    }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color || '#9ca3af', flexShrink: 0 }} />
        {label || '-'}
    </span>
);

const ModernSelect = ({ value, onChange, options, placeholder, icon }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = options.find(o => String(o.id) === String(value));

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(v => !v)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 9, cursor: 'pointer',
                background: '#fff', border: '1.5px solid #e5e7eb',
                fontSize: 14, color: selected ? '#1f2937' : '#9ca3af',
                textAlign: 'left', outline: 'none',
                transition: 'border-color 0.15s',
            }}
                onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
                onBlur={e => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
                {icon && <i className={icon} style={{ fontSize: 12, color: selected ? '#4f46e5' : '#9ca3af', flexShrink: 0 }} />}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selected ? selected.name : placeholder}
                </span>
                <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }} />
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
                    background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.10)', overflow: 'hidden',
                    maxHeight: 220, overflowY: 'auto',
                }}>
                    {options.map((o) => {
                        const isActive = String(o.id) === String(value);
                        return (
                            <div key={o.id} onClick={() => { onChange(o.id); setOpen(false); }} style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: 13,
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: isActive ? '#eef2ff' : '#fff',
                                color: isActive ? '#4f46e5' : '#374151',
                                fontWeight: isActive ? 600 : 400,
                                transition: 'background 0.1s',
                            }}
                                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
                            >
                                {isActive && <i className="fa fa-check" style={{ fontSize: 10, color: '#4f46e5', flexShrink: 0 }} />}
                                {!isActive && <span style={{ width: 10, flexShrink: 0 }} />}
                                {o.name}
                            </div>
                        );
                    })}
                    {options.length === 0 && (
                        <div style={{ padding: '12px 14px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>ไม่พบข้อมูล</div>
                    )}
                </div>
            )}
        </div>
    );
};

const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, marginTop: 4 }}>
        {children}
    </div>
);

const Products = () => {
    const { messages } = useSignalR();
    const api = useApiServices();
    const { t } = useLanguage();
    const [categories, setCategories] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [data, setData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [file, setFile] = useState(null);
    const [keyword, setKeyword] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        nameTh: '', nameEn: '', descriptionTh: '', descriptionEn: '',
        price: 0, sku: '', sequence: 0, image: null, categoryId: null, productGroupId: null,
    });

    const fetchData = async (page = 1, kw = keyword) => {
        const params = { pageNumber: page, pageSize: 10, keyword: kw || undefined };
        const { isSuccess, result } = await api.GetProductsAsync(params);
        if (isSuccess) setData(result);
    };

    const fetchCategories = async () => {
        const [catRes, groupRes] = await Promise.allSettled([
            api.GetCategoriesAsync(),
            api.GetProductGroupsAsync(),
        ]);
        if (catRes.status === 'fulfilled' && catRes.value?.isSuccess) {
            const cats = catRes.value.result || [];
            setCategories(cats);
            setFormData(prev => ({ ...prev, categoryId: cats?.[0]?.id ?? null }));
        }
        if (groupRes.status === 'fulfilled' && groupRes.value?.isSuccess) {
            setProductGroups(groupRes.value.result || []);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setKeyword(searchInput);
        fetchData(1, searchInput);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'sequence' ? parseInt(value) : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const payload = { ...formData, image: file?.id ?? null };
        const { isSuccess } = await api.AddProductsAsync(payload);
        setSubmitting(false);
        if (isSuccess) {
            toast.success('เพิ่มสินค้าสำเร็จ');
            setShowModal(false);
            setFormData({ nameTh: '', nameEn: '', descriptionTh: '', descriptionEn: '', price: 0, sku: '', sequence: 0, image: null, categoryId: null, productGroupId: null });
            setFile(null);
            fetchData();
        } else {
            toast.error('เกิดข้อผิดพลาด');
        }
    };

    useEffect(() => { fetchData(); }, []);
    useEffect(() => { if (showModal) fetchCategories(); }, [showModal]);
    useEffect(() => {
        if (!messages?.length) return;
        const last = messages[messages.length - 1];
        if (last && (last.type === 'Transaction' || last.type === 'Payment') && data?.currentPage === 1) fetchData();
    }, [messages]);

    const items = data.items || [];

    return (
        <Fragment>
            <Row>
                <Col sm="12">
                    <Card style={cardStyle}>
                        <Card.Header style={cardHeaderStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={titleDotStyle} />
                                <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e3a5f' }}>{t('products_title')}</h5>
                                {data.totalItems > 0 && <span style={countBadgeStyle}>{data.totalItems}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
                                    <div style={searchWrapStyle}>
                                        <i className="fa fa-search" style={searchIconStyle} />
                                        <input type="text" placeholder={t('search_products')} value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)} style={searchInputStyle} />
                                    </div>
                                    {keyword && (
                                        <button type="button" style={clearBtnStyle} onClick={() => { setSearchInput(''); setKeyword(''); fetchData(1, ''); }}>
                                            <i className="fa fa-times" />
                                        </button>
                                    )}
                                </form>
                                <button onClick={() => setShowModal(true)} style={addBtnStyle}>
                                    <i className="fa fa-plus" style={{ fontSize: 10 }} />{t('add_product')}
                                </button>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: 0 }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            {['', t('col_sku'), t('col_product_name'), t('col_category'), t('col_status'), t('col_date')].map(h => (
                                                <th key={h} style={{ ...thStyle, width: h === '' ? 60 : undefined }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length === 0 ? (
                                            <tr><td colSpan={6} style={emptyStyle}>
                                                <i className="fas fa-box-open" style={{ fontSize: 24, marginBottom: 8, display: 'block', opacity: 0.3 }} />
                                                {t('no_data')}
                                            </td></tr>
                                        ) : items.map(row => (
                                            <tr key={row.id}
                                                onMouseEnter={() => setHoveredRow(row.id)}
                                                onMouseLeave={() => setHoveredRow(null)}
                                                style={{ background: hoveredRow === row.id ? '#f9fafb' : '#fff', transition: 'background 0.1s' }}>
                                                <td style={{ ...tdStyle, width: 60 }}>
                                                    {row.image ? <ImageViewer id={row.image} width={40} preview={false} /> : (
                                                        <div style={{ width: 40, height: 40, borderRadius: 8, background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="fa fa-image" style={{ color: '#d1d5db', fontSize: 14 }} />
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    <Link to={`/products/${row.id}`} style={{ ...linkStyle, fontFamily: 'monospace', fontSize: 12 }}>{row.sku || '-'}</Link>
                                                </td>
                                                <td style={tdStyle}>
                                                    <Link to={`/products/${row.id}`} style={linkStyle}>{row.nameTh || '-'}</Link>
                                                    {row.nameEn && <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.nameEn}</div>}
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={{ fontSize: 12, background: '#f3f4f6', borderRadius: 6, padding: '2px 8px', color: '#6b7280' }}>{row.category?.name || '-'}</span>
                                                </td>
                                                <td style={tdStyle}><StatusPill color={row.status?.color} label={row.status?.labelTh || row.status?.name} /></td>
                                                <td style={{ ...tdStyle, color: '#9ca3af', fontSize: 12 }}>{DateTimeFormat(row.createDate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ padding: '12px 20px' }}>
                                <Pagination currentPage={data.currentPage} totalPages={data.totalPages} onPageChange={(page) => fetchData(page)} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '18px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <i className="fa fa-cubes" style={{ fontSize: 16, color: '#fff' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>{t('add_product')}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{t('create_product')}</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px' }}>
                    <Form onSubmit={handleSubmit}>

                        {/* Image */}
                        <SectionLabel>{t('product_image')}</SectionLabel>
                        <div style={{ marginBottom: 20 }}>
                            <ImageUpload forceCrop={true} onUploadComplete={(f) => setFile(f)} />
                        </div>

                        {/* Classification */}
                        <SectionLabel>{t('product_basic_info')}</SectionLabel>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Label style={labelStyle}>{t('col_category')}</Form.Label>
                                <ModernSelect
                                    value={formData.categoryId}
                                    onChange={(id) => setFormData(prev => ({ ...prev, categoryId: id }))}
                                    options={categories}
                                    placeholder={t('select_category')}
                                    icon="fa fa-tag"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label style={labelStyle}>{t('col_product_group')}</Form.Label>
                                <ModernSelect
                                    value={formData.productGroupId}
                                    onChange={(id) => setFormData(prev => ({ ...prev, productGroupId: id }))}
                                    options={productGroups}
                                    placeholder={t('select_product_group')}
                                    icon="fa fa-folder-o"
                                />
                            </Col>
                        </Row>

                        {/* Names */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('name_th')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                    <Form.Control name="nameTh" value={formData.nameTh} onChange={handleChange} required style={inputStyle} placeholder="ชื่อสินค้าภาษาไทย" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('name_en')}</Form.Label>
                                    <Form.Control name="nameEn" value={formData.nameEn} onChange={handleChange} style={inputStyle} placeholder="Product name in English" />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Descriptions */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('description_th')}</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="descriptionTh" value={formData.descriptionTh} onChange={handleChange} style={{ ...inputStyle, resize: 'none' }} placeholder="รายละเอียดสินค้าภาษาไทย" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('description_en')}</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="descriptionEn" value={formData.descriptionEn} onChange={handleChange} style={{ ...inputStyle, resize: 'none' }} placeholder="Product description in English" />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Pricing & SKU */}
                        <SectionLabel>{t('product_pricing_info')}</SectionLabel>
                        <Row className="mb-2">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('default_price')}</Form.Label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#6b7280', fontWeight: 600, pointerEvents: 'none' }}>฿</span>
                                        <Form.Control name="price" type="number" value={formData.price} onChange={handleChange} style={{ ...inputStyle, paddingLeft: 24 }} />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('col_sku')}</Form.Label>
                                    <Form.Control name="sku" value={formData.sku} onChange={handleChange} style={{ ...inputStyle, fontFamily: 'monospace' }} placeholder="SKU-001" />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('sequence')}</Form.Label>
                                    <Form.Control name="sequence" type="number" value={formData.sequence} onChange={handleChange} style={inputStyle} />
                                </Form.Group>
                            </Col>
                        </Row>

                    </Form>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '14px 24px', gap: 8 }}>
                    <Button variant="light" onClick={() => setShowModal(false)} style={{ fontSize: 14, borderRadius: 8 }}>{t('cancel')}</Button>
                    <Button onClick={handleSubmit} variant="primary" disabled={submitting} style={{ fontSize: 14, borderRadius: 8, fontWeight: 600, minWidth: 110 }}>
                        {submitting ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />{t('saving')}</> : t('create_product')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

const cardStyle = { borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' };
const cardHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#fff', borderRadius: '12px 12px 0 0' };
const titleDotStyle = { width: 10, height: 10, borderRadius: '50%', background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', flexShrink: 0 };
const countBadgeStyle = { fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '1px 7px' };
const searchWrapStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
const searchIconStyle = { position: 'absolute', left: 10, color: '#9ca3af', fontSize: 12, pointerEvents: 'none' };
const searchInputStyle = { paddingLeft: 30, paddingRight: 12, paddingTop: 6, paddingBottom: 6, border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', minWidth: 200, color: '#374151' };
const clearBtnStyle = { padding: '6px 10px', background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 12 };
const addBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 16px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#1f2937', verticalAlign: 'middle' };
const linkStyle = { color: '#1e3a5f', fontWeight: 600, textDecoration: 'none' };
const emptyStyle = { padding: '48px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13, borderBottom: 'none' };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = { fontSize: 14, borderRadius: 8, border: '1.5px solid #e5e7eb' };

export default Products;
